import {networkInterfaces} from 'node:os';
import {ADDRCONFIG, type LookupAddress} from 'node:dns';
import {lookup, lookupService} from 'node:dns/promises';

import {textToNumericFormatV4, textToNumericFormatV6} from './internal/IpAddressUtil.ts';
import {readFileSync} from "node:fs";

const INET_ADDRESS_V4_SIZE = 4;

class IpAddress {
    readonly address: string;
    readonly family: 4 | 6;
    private _hostname?: string;

    constructor(host: string | undefined, address: string, family: 4 | 6) {
        this._hostname = host;
        this.address = address;
        this.family = family;
    }

    get addressBytes(): Uint8Array {
        if (this.family === 4) {
            return textToNumericFormatV4(this.address);
        }
        //return textToNumericFormatV6(this.address);
    }

    async getHost(): Promise<string> {
        if (!this._hostname) {
            this._hostname = (await lookupService(this.address, 0)).hostname;
        }
        return this._hostname;
    }

    async getIpAddress(nonLoopback: boolean = false): Promise<LookupAddress> {
        const hints = nonLoopback ? ADDRCONFIG : undefined;
        return await lookup(this.address, {family: this.family, hints: hints});
    }
}

export type {IpAddress};

export function isLoopback(address: IpAddress): boolean {
    return address.family === 4 && address.address.startsWith('127.') || address.family === 6 && address.address.startsWith('::1');
}

export async function getByName(host: string): Promise<IpAddress> {
    const {address, family} = await lookup(host, {family: 4});
    if (family !== 4 && family !== 6) {
        throw new Error('unexpected address type');
    }
    return new IpAddress(undefined, address, family);
}

let _anyLocalAddress: IpAddress;

export function anyLocalAddress(): IpAddress {
    if (!_anyLocalAddress) {
        _anyLocalAddress = getByAddress('0.0.0.0', 0);
    }
    return _anyLocalAddress;
}

export function getByAddress(host: string, bytes: Uint8Array | number): IpAddress {
    if (typeof bytes === 'number') {
        const n = bytes;
        bytes = new Uint8Array(INET_ADDRESS_V4_SIZE);
        new DataView(bytes.buffer).setInt32(0, n);
    }
    const family = bytes.length === INET_ADDRESS_V4_SIZE ? 4 : 6;
    const address = Array.from(bytes).join('.');
    return new IpAddress(host, address, family);
}

export async function resolveByAddress(host: string, address: string): Promise<IpAddress> {
    const byName = await getByName(address);
    // todo: check if address and host names are correct
    return new IpAddress(host, byName.address, byName.family);
}

export async function getAllByName(host: string): Promise<IpAddress[]> {
    const addresses = await lookup(host, {all: true});
    return addresses.map(({address, family}) => {
        if (family !== 4 && family !== 6) {
            throw new Error('unexpected address type');
        }
        return new IpAddress(undefined, address, family);
    });
}

export interface NetworkInterface {
    name: string
    loopback: boolean
    addresses: Array<IpAddress>
}

export function getNetworkInterfaces(): NetworkInterface[] {
    return Object.entries(networkInterfaces()).map(([name, addresses]) => {
        return {
            name,
            loopback: addresses.some(info => info.internal),
            addresses: addresses.map(info => {
                return new IpAddress(undefined, info.address, info.family === 'IPv6' ? 6 : 4);
            })
        };
    });
}

function soMaxConn() {
    let somaxconn;
    if (process.platform === 'win32') {
        somaxconn = 200;
    }
    else if (process.platform === 'darwin') {
        somaxconn = 128;
    }
    else {
        somaxconn = 4096;
    }

    try {
        somaxconn = parseInt(readFileSync('/proc/sys/net/core/somaxconn').toString());
    } catch (error) {
        // todo log
    }
    return somaxconn;
}

export const SOMAXCONN = soMaxConn();

